import {
  type Filter,
  FilterId,
  FilterType,
  type SingleValueFilter,
} from '../types/filters';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Reads the skill taxonomy id from the active filter map for AI Search API requests.
 * Used by both the AI Search filter form and the AI Assistant when requesting filter search.
 */
export function getSkillTaxonomyIdFromActiveFilterMap(
  activeFilterMap: Map<FilterId, Filter>
): number {
  const skillTaxonomyFilter = activeFilterMap.get(FilterId.SKILL_TAXONOMY);
  if (!skillTaxonomyFilter || skillTaxonomyFilter.type !== FilterType.VALUE) {
    return 0;
  }
  const value = (skillTaxonomyFilter as SingleValueFilter<unknown>).value;
  if (!isRecord(value)) return 0;
  const taxonomyId = value['taxonomyId'];
  return typeof taxonomyId === 'number' && Number.isFinite(taxonomyId)
    ? taxonomyId
    : 0;
}

/**
 * Reads the role taxonomy id from the active filter map for AI Search API requests.
 * Used by both the AI Search filter form and the AI Assistant when requesting filter search.
 */
export function getRoleTaxonomyIdForAiSearch(
  activeFilterMap: Map<FilterId, Filter>
): string {
  const roleTaxonomyFilter = activeFilterMap.get(FilterId.ROLE_TAXONOMY);
  if (!roleTaxonomyFilter || roleTaxonomyFilter.type !== FilterType.VALUE) {
    return 'custom_role_default';
  }
  const value = (roleTaxonomyFilter as SingleValueFilter<unknown>).value;
  if (!isRecord(value)) return 'custom_role_default';
  if (value['default'] === true) return 'custom_role_default';
  const ricsK50Id = value['ricsK50Id'];
  if (typeof ricsK50Id === 'string' && ricsK50Id.trim() && ricsK50Id !== '0') {
    return `custom_role_rics_k50_${ricsK50Id}`;
  }
  const ricsK10Id = value['ricsK10Id'];
  if (typeof ricsK10Id === 'string' && ricsK10Id.trim() && ricsK10Id !== '0') {
    return `custom_role_rics_k10_${ricsK10Id}`;
  }
  return 'custom_role_default';
}
